/**
 * Question composer.
 *
 * `dir="auto"` on the textarea, deliberately: an employee typing Arabic should see Arabic behave
 * as Arabic while typing, and the browser handles that natively as characters arrive. This is the
 * one place `auto` is the right choice — elsewhere the direction is decided from the whole string,
 * because there the string already exists.
 *
 * Enter sends, Shift+Enter inserts a newline. That is what a chat interface has trained everyone
 * to expect, and getting it backwards means a multi-line question gets sent halfway through.
 *
 * The field and its actions share one surface that carries the focus state, so the whole control
 * reads as one thing. That is why the textarea is styled here rather than taken from the `Textarea`
 * primitive: the primitive owns its own border and a visible label, and this design has neither.
 * The label still exists — it is the field's accessible name — it is just not painted.
 *
 * ## The resting state is filled, and the focused state is not
 *
 * The new design gives the composer a tinted ground with no border at rest, and swaps to white with
 * a blue border on focus. That is the opposite of the usual arrangement and it is the right way
 * round here: at rest the composer has to read as *the place to type* on an otherwise white page,
 * which a tint does and a hairline border does not; once you are typing, the page has already told
 * you where you are and the tint is just noise behind your own words.
 */

import { type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { AttachIcon, Button, SendIcon } from "@nab/shared";

import { DictateButton } from "./DictateButton";

export interface ComposerProps {
  onSend: (question: string) => void;
  onStop: () => void;
  sending: boolean;
  disabled?: boolean;
  /** The conversation, so a dictated question is masked in the same scope as a typed one. */
  sessionId?: string | null;
}

export function Composer({
  onSend,
  onStop,
  sending,
  disabled = false,
  sessionId = null,
}: ComposerProps): JSX.Element {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();
  const [value, setValue] = useState("");

  const submit = (): void => {
    const question = value.trim();
    if (!question || sending) return;
    onSend(question);
    setValue("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label className="sr-only" htmlFor="composer-input">
        {t("chat.inputLabel")}
      </label>

      {/* The whole control carries the focus state, not the field inside it. The design's own
          arrangement, and the right way round: at rest the tint says "type here" on an otherwise
          white page, and once you are typing the page has already told you where you are. */}
      <div className="flex items-center gap-2 rounded-xl border border-transparent bg-surfaceSunken py-2 pe-2 ps-2 transition-all focus-within:border-primary focus-within:bg-surfaceRaised sm:pe-2 sm:ps-4">
        {/* Present because the design shows it, and disabled because uploading a document from the
            assistant is not in M1 — the corpus is curated through the back office. A control that
            looks live and does nothing is worse than one that says it is not ready yet. */}
        <button
          type="button"
          disabled
          title={t("chat.attachUnavailable")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-onPrimaryMuted disabled:cursor-not-allowed"
        >
          <AttachIcon />
          <span className="sr-only">{t("chat.attachUnavailable")}</span>
        </button>

        <textarea
          id="composer-input"
          // No `focus:outline-none`: the container's border change is a mouse-user affordance, and
          // the base stylesheet's `:focus-visible` ring is what a keyboard user needs on top of it.
          // `outline-none` here and only here: the container's border is the focus affordance, and
          // a second ring inside it draws a box within a box. The container is what a keyboard user
          // sees change, so nothing is lost.
          className="max-h-[160px] min-h-[44px] flex-1 resize-none border-0 bg-transparent px-1 py-2 text-body-md tracking-tight text-textPrimary outline-none placeholder:text-onPrimaryMuted"
          rows={1}
          value={value}
          placeholder={t("chat.inputPlaceholder")}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          // The browser mirrors the field as the employee types, per keystroke.
          dir="auto"
        />

        {/* UC-A6. Absent where the deployment has no speech engine, which is the default —
            see docs/adr/0008-voice-input-and-read-aloud.md. The transcript lands in the field
            for the employee to read and correct; it never sends. */}
        <DictateButton
          onTranscript={(heard) =>
            // Appended rather than replacing: an employee who typed half a question and then
            // dictated the rest should get both, and losing typed text to a microphone press is
            // the kind of thing that stops a feature being used.
            setValue((current) => (current.trim() ? `${current.trim()} ${heard}` : heard))
          }
          sessionId={sessionId}
          disabled={disabled || sending}
        />

        {sending ? (
          <Button type="button" variant="secondary" size="sm" onClick={onStop}>
            {common("actions.cancel")}
          </Button>
        ) : (
          // Icon-only, as drawn. The accessible name comes from `sr-only` text rather than from a
          // `title`, so it is announced rather than merely hoverable.
          <Button
            type="submit"
            variant="primary"
            disabled={disabled || !value.trim()}
            className="h-9 w-9 shrink-0 rounded-lg p-0"
          >
            <SendIcon />
            <span className="sr-only">{common("actions.send")}</span>
          </Button>
        )}
      </div>
    </form>
  );
}
